/**
 * One combatant on screen: a skeleton clone of its chassis, its livery (body and kit node
 * materials with a pre-built cloak pair so cloaking is a swap), heat glow and hit flash through
 * the livery uniforms, cosmetic part visibility, LOD switching over the body and kit meshes of
 * each level, and the animation driver (locomotion, shoot overlay, melee, flinch, death).
 */
import {
  AdditiveAnimationBlendMode,
  AnimationClip,
  AnimationMixer,
  AnimationUtils,
  LoopOnce,
  type AnimationAction,
  type Mesh,
  type MeshStandardNodeMaterial,
  type Object3D,
  type SkinnedMesh,
  Vector3,
} from 'three/webgpu';
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';

import type { LocomotionChoice, LocomotionClip } from '../sim/locomotion.js';
import { Livery } from './livery.js';
import { partIdOfNode, type MechLook } from './look.js';
import { isBodyMesh, lodGroupsOf, meshesOf, type MechAsset } from './mechAsset.js';

/** Camera distances at which LOD1 and LOD2 take over (metres). */
export const LOD_DISTANCES = [0, 25, 60] as const;
const FADE = 0.15;
/** Bones the shoot and flinch overlays may move (legs keep the locomotion clip). */
const UPPER_BODY =
  /^(Head|Neck|Chest|Torso|Shoulder|UpperArm|LowerArm|Palm|Index|Pinky|Ring|Thumb)/;
const SHOOT_MIN_INTERVAL = 0.15;
const FLINCH_MIN_INTERVAL = 0.4;
const FLASH_SECONDS = 0.15;

export type MeleeStyle = 'punch' | 'slash' | 'kick';

/**
 * The upper-body subset of a clip with every track cloned: the additive conversion rewrites
 * values in place, and the asset pipeline's accessor dedup makes clips share the buffers of
 * identical tracks (every clip's constant bone scales, for one), so an in-place edit would
 * corrupt the locomotion clips too.
 */
function upperBodyClip(clip: AnimationClip): AnimationClip | null {
  const tracks = clip.tracks
    .filter((t) => UPPER_BODY.test(t.name.split('.')[0] ?? ''))
    .map((t) => t.clone());
  if (tracks.length === 0) return null;
  return new AnimationClip(`${clip.name}_upper`, clip.duration, tracks, clip.blendMode);
}

const _local = new Vector3();
const _size = new Vector3();

/** A weapon model's longest extent, most of which is barrel. */
function barrelLengthOf(node: Object3D | null): number {
  const mesh = node as Mesh | null;
  if (!mesh?.isMesh) return 0;
  mesh.geometry.computeBoundingBox();
  const size = mesh.geometry.boundingBox?.getSize(_size);
  return size ? Math.max(size.x, size.y, size.z) * 0.9 : 0;
}

export class MechView {
  readonly root: Object3D;
  /** Skinned meshes per LOD level (the body and the kit of that level). */
  readonly lods: SkinnedMesh[][];
  readonly mixer: AnimationMixer;
  readonly height: number;
  private readonly livery: Livery;
  private readonly clips: Map<string, AnimationClip>;
  private readonly bodyMeshes: Mesh[];
  private readonly kitMeshes: Mesh[];
  private readonly cosmetics: Map<string, Mesh[]>;
  private parts: readonly string[] = [];
  private readonly locomotion = new Map<LocomotionClip, AnimationAction>();
  private readonly melee = new Map<MeleeStyle, AnimationAction>();
  private shootAction: AnimationAction | null = null;
  private flinchAction: AnimationAction | null = null;
  private deathAction: AnimationAction | null = null;
  private current: AnimationAction | null = null;
  private currentName: LocomotionClip | null = null;
  private meleeActive: AnimationAction | null = null;
  private dead = false;
  private cloaked = false;
  private heat = 0;
  private flashLeft = 0;
  /** The primary weapon's model, for beams and tracers to start at its barrel tip. */
  private readonly weaponNode: Object3D | null;
  private barrelLength = 0;
  /** The alternate primary's model (Phase 8), shown instead of the stock one when equipped. */
  private readonly altWeaponNode: Object3D | null;
  private altBarrelLength = 0;
  private alternate = false;
  private sinceShoot = 10;
  private sinceFlinch = 10;
  private activeLod = -1;

  constructor(asset: MechAsset, look: MechLook) {
    this.root = cloneSkeleton(asset.template);
    this.height = asset.height;
    this.lods = lodGroupsOf(this.root, asset.sidecar);
    const meshes = meshesOf(this.root);
    this.bodyMeshes = meshes.filter(isBodyMesh);
    this.kitMeshes = meshes.filter((m) => !isBodyMesh(m));
    this.cosmetics = new Map();
    for (const mesh of meshes) {
      const part = partIdOfNode(mesh.name);
      if (part === null) continue;
      const list = this.cosmetics.get(part) ?? [];
      list.push(mesh);
      this.cosmetics.set(part, list);
    }
    this.clips = asset.clips;
    this.weaponNode = this.root.getObjectByName('WEAPON_primary') ?? null;
    this.barrelLength = barrelLengthOf(this.weaponNode);
    this.altWeaponNode = this.root.getObjectByName('WEAPON_ALT_primary') ?? null;
    this.altBarrelLength = barrelLengthOf(this.altWeaponNode);
    this.livery = new Livery(asset.atlas, asset.maps, look.livery);
    this.applyMaterials(false);
    this.setParts(look.parts);
    this.setWeapon(look.alternatePrimary);

    this.mixer = new AnimationMixer(this.root);
    const clip = (name: string): AnimationClip | undefined => asset.clips.get(name);
    for (const name of ['Idle', 'Walk', 'Run', 'Jump'] as const) {
      const c = clip(name);
      if (!c) continue;
      const action = this.mixer.clipAction(c);
      if (name === 'Jump') {
        action.setLoop(LoopOnce, 1);
        action.clampWhenFinished = true;
      }
      this.locomotion.set(name, action);
    }
    const idle = clip('Idle');
    const shoot = clip('Shoot');
    if (shoot && idle) {
      const upper = upperBodyClip(shoot);
      if (upper) {
        AnimationUtils.makeClipAdditive(upper, 0, idle);
        upper.blendMode = AdditiveAnimationBlendMode;
        this.shootAction = this.mixer.clipAction(upper);
        this.shootAction.setLoop(LoopOnce, 1);
        this.shootAction.clampWhenFinished = false;
      }
    }
    const flinch = clip('HitRecieve_1');
    if (flinch && idle) {
      const upper = upperBodyClip(flinch);
      if (upper) {
        AnimationUtils.makeClipAdditive(upper, 0, idle);
        upper.blendMode = AdditiveAnimationBlendMode;
        this.flinchAction = this.mixer.clipAction(upper);
        this.flinchAction.setLoop(LoopOnce, 1);
      }
    }
    for (const [style, name] of [
      ['punch', 'Punch'],
      ['slash', 'SwordSlash'],
      ['kick', 'Kick'],
    ] as const) {
      const c = clip(name);
      if (!c) continue;
      const action = this.mixer.clipAction(c);
      action.setLoop(LoopOnce, 1);
      action.clampWhenFinished = false;
      this.melee.set(style, action);
    }
    const death = clip('Death');
    if (death) {
      this.deathAction = this.mixer.clipAction(death);
      this.deathAction.setLoop(LoopOnce, 1);
      this.deathAction.clampWhenFinished = true;
    }
    this.mixer.addEventListener('finished', (e) => {
      if (e.action === this.meleeActive) {
        this.meleeActive = null;
        this.current?.reset().fadeIn(FADE).play();
      }
    });
    this.setLocomotion({ clip: 'Idle', timeScale: 1 });
    this.updateLod(this.root.position);
  }

  setPose(x: number, y: number, z: number, yaw: number): void {
    this.root.position.set(x, y, z);
    this.root.rotation.y = yaw;
  }

  setLocomotion(choice: LocomotionChoice): void {
    if (this.dead) return;
    const next = this.locomotion.get(choice.clip) ?? this.locomotion.get('Idle');
    if (!next) return;
    if (choice.clip !== this.currentName) {
      if (!this.meleeActive) {
        next.reset().fadeIn(FADE).play();
        this.current?.fadeOut(FADE);
      }
      this.current = next;
      this.currentName = choice.clip;
    }
    next.timeScale = choice.timeScale;
  }

  /** A shot fired: the upper-body overlay, rate-limited and held under sustained fire. */
  shoot(): void {
    if (!this.shootAction || this.dead) return;
    if (this.sinceShoot < SHOOT_MIN_INTERVAL && this.shootAction.isRunning()) return;
    this.sinceShoot = 0;
    this.shootAction.reset().setEffectiveWeight(1).play();
  }

  playMelee(style: MeleeStyle): void {
    if (this.dead) return;
    const action = this.melee.get(style) ?? this.melee.get('punch');
    if (!action) return;
    this.meleeActive = action;
    this.current?.fadeOut(FADE * 0.5);
    action
      .reset()
      .setEffectiveWeight(1)
      .fadeIn(FADE * 0.5)
      .play();
  }

  /** A one-shot full-body clip (the hangar's idle emotes); the locomotion resumes after it. */
  playEmote(name: string): boolean {
    if (this.dead || this.meleeActive) return false;
    const clip = this.clips.get(name);
    if (!clip) return false;
    const action = this.mixer.clipAction(clip);
    action.setLoop(LoopOnce, 1);
    action.clampWhenFinished = false;
    this.meleeActive = action;
    this.current?.fadeOut(FADE);
    action.reset().setEffectiveWeight(1).fadeIn(FADE).play();
    return true;
  }

  flinch(): void {
    if (!this.flinchAction || this.dead || this.sinceFlinch < FLINCH_MIN_INTERVAL) return;
    this.sinceFlinch = 0;
    this.flinchAction.reset().setEffectiveWeight(0.7).play();
    this.flashLeft = FLASH_SECONDS;
  }

  die(): void {
    if (this.dead) return;
    this.dead = true;
    this.meleeActive = null;
    for (const a of this.locomotion.values()) a.fadeOut(FADE);
    this.shootAction?.stop();
    this.flinchAction?.stop();
    if (this.deathAction) this.deathAction.reset().setEffectiveWeight(1).fadeIn(FADE).play();
  }

  /** Round reset: back to a standing, uncloaked, cool mech. */
  revive(): void {
    this.dead = false;
    this.livery.clearScorches();
    this.deathAction?.stop();
    this.meleeActive = null;
    this.currentName = null;
    this.setLocomotion({ clip: 'Idle', timeScale: 1 });
    this.setCloak(false);
    this.heat = 0;
    this.flashLeft = 0;
  }

  setCloak(on: boolean): void {
    if (on === this.cloaked) return;
    this.cloaked = on;
    this.applyMaterials(on);
  }

  /** Heat as a fraction of the cap drives the emissive glow. */
  setHeat(fraction: number): void {
    this.heat = Math.max(0, Math.min(1, fraction));
  }

  /** What the mixer is playing (diagnostics). */
  animation(): { clip: string | null; time: number; weight: number; running: boolean } {
    const action = this.meleeActive ?? this.current;
    return {
      clip: action ? action.getClip().name : null,
      time: action?.time ?? 0,
      weight: action?.getEffectiveWeight() ?? 0,
      running: action?.isRunning() ?? false,
    };
  }

  /** The livery and the cosmetic parts as drawn (the e2e reads this back through the debug API). */
  look(): MechLook {
    return {
      livery: this.livery.current(),
      parts: [...this.parts],
      alternatePrimary: this.alternate,
    };
  }

  /** Repaints and re-equips without touching the animation or the materials' pipelines. */
  setLook(look: MechLook): void {
    this.livery.set(look.livery);
    this.setParts(look.parts);
    this.setWeapon(look.alternatePrimary);
  }

  /** Shows the alternate primary's model instead of the stock one (or the stock when absent). */
  setWeapon(alternate: boolean): void {
    this.alternate = alternate && this.altWeaponNode !== null;
    if (this.weaponNode) this.weaponNode.visible = !this.alternate;
    if (this.altWeaponNode) this.altWeaponNode.visible = this.alternate;
  }

  /** Whether the alternate primary's model is the one shown. */
  get alternateWeapon(): boolean {
    return this.alternate;
  }

  /** Shows the named cosmetic parts and hides every other `COSMETIC_*` node. */
  setParts(parts: readonly string[]): void {
    this.parts = [...parts];
    for (const [part, meshes] of this.cosmetics) {
      const visible = parts.includes(part);
      for (const mesh of meshes) mesh.visible = visible;
    }
  }

  /** Where a beam or tracer starts: the primary weapon's barrel tip along the aim. */
  muzzleWorld(aim: { x: number; y: number; z: number }, out: Vector3): boolean {
    const node = this.alternate ? this.altWeaponNode : this.weaponNode;
    const length = this.alternate ? this.altBarrelLength : this.barrelLength;
    if (!node) return false;
    node.getWorldPosition(out);
    out.x += aim.x * length;
    out.y += aim.y * length;
    out.z += aim.z * length;
    return true;
  }

  /** A hit at a world point leaves a scorch mark in the livery (bind-space approximation). */
  addScorch(world: { x: number; y: number; z: number }, radius = 0.5): void {
    _local.set(world.x, world.y, world.z);
    this.root.worldToLocal(_local);
    this.livery.addScorch(_local, radius);
  }

  scorchCount(): number {
    return this.livery.scorchCount();
  }

  /** Advances animations and the transient glow; call once per rendered frame. */
  update(dt: number): void {
    this.sinceShoot += dt;
    this.sinceFlinch += dt;
    this.flashLeft = Math.max(0, this.flashLeft - dt);
    this.mixer.update(dt);
    this.livery.update(dt);
    this.livery.setGlow(this.heat, this.flashLeft > 0 ? this.flashLeft / FLASH_SECONDS : 0);
  }

  updateLod(cameraPosition: Vector3): void {
    const d = cameraPosition.distanceTo(this.root.position);
    let level = 0;
    for (let i = LOD_DISTANCES.length - 1; i > 0; i--) {
      if (d >= (LOD_DISTANCES[i] ?? Infinity) && this.lods[i]) {
        level = i;
        break;
      }
    }
    if (level === this.activeLod) return;
    this.activeLod = level;
    this.lods.forEach((group, i) => {
      for (const mesh of group) mesh.visible = i === level;
    });
  }

  /** Every material, so the renderer can compile the cloak pipelines before the match starts. */
  materials(): MeshStandardNodeMaterial[] {
    return this.livery.materials();
  }

  dispose(): void {
    this.mixer.stopAllAction();
    this.livery.dispose();
    this.root.removeFromParent();
  }

  private applyMaterials(cloak: boolean): void {
    const body = cloak ? this.livery.body.cloak : this.livery.body.opaque;
    const kit = cloak ? this.livery.kit.cloak : this.livery.kit.opaque;
    for (const mesh of this.bodyMeshes) {
      mesh.material = body;
      mesh.castShadow = !cloak;
    }
    for (const mesh of this.kitMeshes) {
      mesh.material = kit;
      mesh.castShadow = !cloak;
    }
  }
}
